'use client';

import { useCallback, useMemo, useState } from 'react';

/**
 * Hook that manages a boolean state with toggle functionality.
 * Provides both toggle and direct set capabilities for boolean values.
 *
 * @param {boolean} [initialValue=false] - The initial boolean state value.
 * @returns {[boolean, (newValue?: boolean) => void]} Tuple containing the current state and toggle function.
 */
export const useToggle = (initialValue = false): [boolean, (newValue?: boolean) => void] => {
  const [on, setOn] = useState<boolean>(initialValue);

  const handleToggle = useCallback((value?: boolean) => {
    if (typeof value === 'boolean') {
      return setOn(value);
    }

    return setOn((v) => !v);
  }, []);

  /*
   * Memoize the returned tuple so consumers (`React.memo`, `useMemo`,
   * context-value equality) see a stable reference whenever neither
   * `on` nor `handleToggle` changes. Without this, every render of the
   * caller would produce a fresh array literal and defeat downstream
   * memoization — see CDS-5649 (FIX-3).
   */
  return useMemo<[boolean, (newValue?: boolean) => void]>(
    () => [on, handleToggle],
    [on, handleToggle],
  );
};
